import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../Layouts/DashboardLayout";
import VendorQuotations from "./VendorQuotations";

const API_URL = "/api";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function StatusBadge({ status }) {
  const normalizedStatus = (status || "").toLowerCase();

  const color =
    normalizedStatus === "paid" ||
    normalizedStatus === "completed" ||
    normalizedStatus === "active"
      ? "bg-emerald-500/20 text-emerald-400"
      : normalizedStatus === "cancelled" ||
          normalizedStatus === "rejected" ||
          normalizedStatus === "overdue"
        ? "bg-red-500/20 text-red-400"
        : "bg-amber-400/10 text-amber-400";

  return (
    <span className={`rounded-full px-3 py-1 text-xs ${color}`}>
      {status || "Pending"}
    </span>
  );
}

export default function VendorProfile() {
  const { id } = useParams();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [tab, setTab] = useState("quotations");
  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError] = useState("");

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [ledger, setLedger] = useState([]);

  useEffect(() => {
    const fetchVendorProfile = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_URL}/vendor/profile/${id}`, {
          credentials: "include",
        });

        const data = await response.json();

        if (data.status === "success") {
          setVendor(data.data);
        } else {
          setError(data.message || "Vendor not found");
        }
      } catch (err) {
        console.error(err);
        setError("Could not connect to the backend");
      } finally {
        setLoading(false);
      }
    };

    fetchVendorProfile();
  }, [id]);

  useEffect(() => {
    if (tab === "quotations") {
      return;
    }

    const fetchTabData = async () => {
      setTabLoading(true);
      setTabError("");

      let endpoint = "";
      let updateData = null;

      if (tab === "orders") {
        endpoint = `${API_URL}/vendor/profile/${id}/purchase-orders`;
        updateData = setPurchaseOrders;
      }

      if (tab === "payments") {
        endpoint = `${API_URL}/vendor/profile/${id}/payments`;
        updateData = setPayments;
      }

      if (tab === "ledger") {
        endpoint = `${API_URL}/vendor/profile/${id}/ledger`;
        updateData = setLedger;
      }

      try {
        const response = await fetch(endpoint, {
          credentials: "include",
        });

        const data = await response.json();

        if (data.status === "success") {
          updateData(data.data || []);
        } else {
          setTabError(data.message || "Could not load data");
        }
      } catch (err) {
        console.error(err);
        setTabError("Could not connect to the backend");
      } finally {
        setTabLoading(false);
      }
    };

    fetchTabData();
  }, [id, tab]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-gray-400">
          Loading vendor profile...
        </div>
      </DashboardLayout>
    );
  }

  if (error || !vendor) {
    return (
      <DashboardLayout>
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-8 text-red-300">
          {error || "Vendor not found"}
        </div>
      </DashboardLayout>
    );
  }

  const contacts = vendor.contacts || [];
  const branches = vendor.branches || [];
  const primaryContact =
    contacts.find((contact) => contact.primary) || contacts[0];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="text-2xl font-serif">{vendor.name}</h1>
            <p className="text-sm text-gray-400">Vendor Profile & History</p>
          </div>

          <div className="flex gap-3">
            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <p className="text-[11px] uppercase tracking-wider text-gray-500">
                Purchase Orders
              </p>
              <p className="font-medium text-amber-400">
                {vendor.summary?.purchase_order_count || 0}
              </p>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <p className="text-[11px] uppercase tracking-wider text-gray-500">
                Pending Payable
              </p>
              <p className="font-medium text-amber-400">
                {formatCurrency(vendor.summary?.pending_payable)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 rounded-2xl border border-white/10 bg-white/5 p-6 md:grid-cols-2">
          <div>
            <p className="text-xs text-gray-400">Vendor Type</p>
            <span
              className="mt-1 inline-block rounded-full px-3 py-1 text-xs"
              style={{
                background: `${vendor.type?.color || "#FACC15"}22`,
                color: vendor.type?.color || "#FACC15",
              }}
            >
              {vendor.type?.name || "Uncategorized"}
            </span>
          </div>

          <div>
            <p className="text-xs text-gray-400">Status</p>
            <div className="mt-1">
              <StatusBadge status={vendor.status} />
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-400">GST Number</p>
            <p>{vendor.gst || "-"}</p>
          </div>

          <div>
            <p className="text-xs text-gray-400">PAN Number</p>
            <p>{vendor.pan || "-"}</p>
          </div>

          <div>
            <p className="text-xs text-gray-400">Branch Coverage</p>
            <p>
              {branches.length > 0
                ? branches.map((branch) => branch.name).join(", ")
                : "-"}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-400">Primary Contact</p>
            <p>{primaryContact?.name || "-"}</p>
            {primaryContact?.phone && (
              <p className="text-sm text-gray-400">{primaryContact.phone}</p>
            )}
            {primaryContact?.email && (
              <p className="text-sm text-gray-400">{primaryContact.email}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <p className="text-xs text-gray-400">Notes</p>
            <p>{vendor.notes || "-"}</p>
          </div>
        </div>

        <div className="flex w-fit gap-2 overflow-x-auto rounded-xl bg-black/40 p-1">
          {["quotations", "orders", "payments", "ledger"].map((item) => (
            <button
              key={item}
              onClick={() => setTab(item)}
              className={`rounded-lg px-5 py-2 text-sm transition ${
                tab === item
                  ? "bg-amber-400 font-medium text-black"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {item.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          {tabLoading && (
            <div className="py-8 text-center text-gray-400">Loading...</div>
          )}

          {!tabLoading && tabError && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">
              {tabError}
            </div>
          )}

          {!tabLoading && !tabError && tab === "quotations" && (
            <VendorQuotations vendorId={vendor.id} />
          )}

          {!tabLoading && !tabError && tab === "orders" && (
            <div className="space-y-5">
              {purchaseOrders.length === 0 ? (
                <EmptyState text="No purchase orders found for this vendor." />
              ) : (
                purchaseOrders.map((order) => (
                  <div
                    key={order.id}
                    className="overflow-hidden rounded-xl border border-white/10"
                  >
                    <div className="flex flex-col justify-between gap-4 bg-black/30 p-5 md:flex-row md:items-center">
                      <div>
                        <p className="font-medium text-amber-400">
                          {order.po_number}
                        </p>
                        <p className="mt-1 text-sm text-gray-400">
                          {order.branch} · Created {formatDate(order.created_at)}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {order.is_awarded_vendor && (
                          <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs text-emerald-400">
                            Awarded Vendor
                          </span>
                        )}

                        <StatusBadge status={order.vendor_po_status || order.status} />
                      </div>
                    </div>

                    <div className="grid gap-4 p-5 text-sm md:grid-cols-3">
                      <div>
                        <p className="text-xs text-gray-500">Delivery Date</p>
                        <p>{formatDate(order.delivery_date)}</p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Priority</p>
                        <p>{order.priority || "-"}</p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Items</p>
                        <p>{order.items?.length || 0}</p>
                      </div>
                    </div>

                    {order.notes && (
                      <div className="border-t border-white/10 px-5 py-3 text-sm text-gray-400">
                        <span className="text-gray-500">Notes: </span>
                        {order.notes}
                      </div>
                    )}

                    <div className="overflow-x-auto border-t border-white/10">
                      <table className="w-full min-w-[800px] text-sm">
                        <thead className="bg-black/20 text-gray-400">
                          <tr>
                            <th className="px-4 py-3 text-left">Item</th>
                            <th className="px-4 py-3 text-left">Category</th>
                            <th className="px-4 py-3 text-right">Ordered</th>
                            <th className="px-4 py-3 text-right">Inward</th>
                            <th className="px-4 py-3 text-right">Pending</th>
                            <th className="px-4 py-3 text-left">Specification</th>
                          </tr>
                        </thead>

                        <tbody>
                          {(order.items || []).map((item) => (
                            <tr
                              key={item.id}
                              className="border-t border-white/10"
                            >
                              <td className="px-4 py-3 font-medium">
                                {item.name}
                              </td>
                              <td className="px-4 py-3 text-gray-400">
                                {item.category || "-"}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {item.qty} {item.unit}
                              </td>
                              <td className="px-4 py-3 text-right text-emerald-400">
                                {item.inward_qty} {item.unit}
                              </td>
                              <td className="px-4 py-3 text-right text-amber-400">
                                {item.pending_qty} {item.unit}
                              </td>
                              <td className="px-4 py-3 text-gray-400">
                                {item.spec || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {!tabLoading && !tabError && tab === "payments" && (
            <div className="overflow-x-auto">
              {payments.length === 0 ? (
                <EmptyState text="No payments found for this vendor." />
              ) : (
                <table className="w-full min-w-[850px] text-sm">
                  <thead className="bg-black/30 text-gray-400">
                    <tr>
                      <th className="px-4 py-3 text-left">PO Number</th>
                      <th className="px-4 py-3 text-left">Branch</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-left">Due Date</th>
                      <th className="px-4 py-3 text-left">Paid On</th>
                      <th className="px-4 py-3 text-left">Method</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {payments.map((payment) => (
                      <tr
                        key={payment.id}
                        className="border-t border-white/10 hover:bg-white/5"
                      >
                        <td className="px-4 py-3 font-medium text-amber-400">
                          {payment.po_number}
                        </td>
                        <td className="px-4 py-3">{payment.branch}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(payment.due_date)}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(payment.paid_on)}
                        </td>
                        <td className="px-4 py-3">
                          {payment.payment_method || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={payment.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!tabLoading && !tabError && tab === "ledger" && (
            <div className="overflow-x-auto">
              {ledger.length === 0 ? (
                <EmptyState text="No inventory inward entries found for finalized purchase orders." />
              ) : (
                <table className="w-full min-w-[800px] text-sm">
                  <thead className="bg-black/30 text-gray-400">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">PO Number</th>
                      <th className="px-4 py-3 text-left">Item</th>
                      <th className="px-4 py-3 text-right">Quantity</th>
                      <th className="px-4 py-3 text-left">Branch</th>
                      <th className="px-4 py-3 text-left">Reference</th>
                    </tr>
                  </thead>

                  <tbody>
                    {ledger.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-t border-white/10 hover:bg-white/5"
                      >
                        <td className="px-4 py-3">{formatDate(entry.date)}</td>
                        <td className="px-4 py-3 font-medium text-amber-400">
                          {entry.po_number}
                        </td>
                        <td className="px-4 py-3">{entry.item}</td>
                        <td className="px-4 py-3 text-right text-emerald-400">
                          +{entry.qty} {entry.unit}
                        </td>
                        <td className="px-4 py-3">{entry.branch}</td>
                        <td className="px-4 py-3 text-gray-400">
                          {entry.reference || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function EmptyState({ text }) {
  return <div className="py-10 text-center text-gray-500">{text}</div>;
}